/**
 * OrganizationalChart.js
 * MEVIA v4.0 - Organigrama Visual Interactivo
 * Árbol organizacional con SVG puro
 */

import React, { useState, useEffect, useRef } from 'react';
import { getToken } from '../utils/tokenStorage';import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { 
  Users, Search, ZoomIn, ZoomOut, Maximize2, 
  User, Mail, Briefcase, Building,
  ChevronRight, X, Link2, Unlink,
  AlertTriangle, CheckCircle, AlertCircle, RefreshCw,
  ChevronDown, ChevronUp, Download, FileText
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import logger from '../utils/logger';

// API helper for this component (uses fetch instead of axios for reliability)
import { API_BASE as API_URL } from '../utils/apiConfig';

const apiCall = async (endpoint, options = {}) => {
  const token = getToken();
  const { headers: customHeaders, ...restOptions } = options;
  
  const response = await fetch(`${API_URL}/api${endpoint}`, {
    ...restOptions,
    credentials: 'include',
    headers: {
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      'Content-Type': 'application/json',
      ...customHeaders
    }
  });
  
  const data = await response.json();
  
  if (!response.ok) {
    throw new Error(data.detail || `HTTP ${response.status}`);
  }
  
  return data;
};

// Level colors
const LEVEL_COLORS = {
  1: { bg: '#1e3a5f', text: '#ffffff', name: 'C-Suite' },
  2: { bg: '#1565c0', text: '#ffffff', name: 'Directores' },
  3: { bg: '#2e7d32', text: '#ffffff', name: 'Gerentes' },
  4: { bg: '#f9a825', text: '#000000', name: 'Coordinadores' },
  5: { bg: '#757575', text: '#ffffff', name: 'Operativos' },
  default: { bg: '#9e9e9e', text: '#ffffff', name: 'Sin nivel' }
};

const IPS_COLORS = {
  healthy: { bg: '#22c55e', label: 'Saludable' },
  at_risk: { bg: '#f59e0b', label: 'En Riesgo' },
  critical: { bg: '#ef4444', label: 'Crítico' }
};

// Tree Node Card Component
const OrgNode = ({ node, onSelect, isSelected, level = 0 }) => {
  const [expanded, setExpanded] = useState(level < 2);
  const colors = LEVEL_COLORS[node.level] || LEVEL_COLORS.default;
  const hasChildren = node.children && node.children.length > 0;
  const ipsColor = node.ips_status ? IPS_COLORS[node.ips_status] : null;

  return (
    <div className="flex flex-col items-center">
      {/* Node Card */}
      <div
        onClick={() => onSelect(node)}
        className={`relative cursor-pointer transition-all duration-200 hover:scale-105 ${
          isSelected ? 'ring-2 ring-teal-500 ring-offset-2' : ''
        }`}
        style={{ minWidth: '180px' }}
      >
        <div
          className="rounded-lg p-3 shadow-md border-2"
          style={{ 
            backgroundColor: colors.bg, 
            borderColor: colors.bg,
            color: colors.text 
          }}
        >
          <div className="flex items-center gap-3">
            {/* Avatar */}
            <div 
              className="w-10 h-10 rounded-full flex items-center justify-center text-lg font-bold"
              style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}
            >
              {node.name?.charAt(0) || '?'}
            </div>
            
            {/* Info */}
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm truncate">{node.name}</p>
              <p className="text-xs opacity-80 truncate">{node.position || 'Sin puesto'}</p>
              {node.department && (
                <p className="text-xs opacity-60 truncate">{node.department}</p>
              )}
            </div>
          </div>
          
          {/* Badges */}
          <div className="flex items-center gap-2 mt-2">
            {node.direct_reports_count > 0 && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-white/20">
                👥 {node.direct_reports_count}
              </span>
            )}
            {node.is_matrix_report && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-purple-500/30 text-purple-100">
                Matrix
              </span>
            )}
          </div>
          
          {/* IPS Badge */}
          {ipsColor && node.ips_score !== null && (
            <div 
              className="absolute -top-2 -right-2 w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white shadow-lg"
              style={{ backgroundColor: ipsColor.bg }}
            >
              {Math.round(node.ips_score)}
            </div>
          )}
        </div>
        
        {/* Expand/Collapse Button */}
        {hasChildren && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setExpanded(!expanded);
            }}
            className="absolute -bottom-3 left-1/2 -translate-x-1/2 w-6 h-6 rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center shadow-md z-10"
          >
            {expanded ? (
              <ChevronUp className="h-4 w-4 text-gray-600" />
            ) : (
              <ChevronDown className="h-4 w-4 text-gray-600" />
            )}
          </button>
        )}
      </div>
      
      {/* Children */}
      {hasChildren && expanded && (
        <div className="relative mt-8">
          {/* Vertical line from parent */}
          <div className="absolute top-0 left-1/2 w-0.5 h-4 bg-gray-300 -translate-x-1/2 -translate-y-4" />
          
          {/* Horizontal line for multiple children */}
          {node.children.length > 1 && (
            <div 
              className="absolute top-0 h-0.5 bg-gray-300"
              style={{
                left: `calc(50% - ${(node.children.length - 1) * 100}px)`,
                width: `${(node.children.length - 1) * 200}px`
              }}
            />
          )}
          
          <div className="flex gap-4 justify-center">
            {node.children.map((child, idx) => (
              <div key={child.id} className="relative">
                {/* Vertical line to child */}
                <div className="absolute top-0 left-1/2 w-0.5 h-4 bg-gray-300 -translate-x-1/2 -translate-y-4" />
                <OrgNode 
                  node={child} 
                  onSelect={onSelect} 
                  isSelected={isSelected}
                  level={level + 1}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const OrganizationalChart = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [treeData, setTreeData] = useState([]);
  const [matrixRelations, setMatrixRelations] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const [employeeDetail, setEmployeeDetail] = useState(null);
  const [showDetailPanel, setShowDetailPanel] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showMatrixModal, setShowMatrixModal] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [selectedManager, setSelectedManager] = useState('');
  const [zoom, setZoom] = useState(1);
  const [translateX, setTranslateX] = useState(0);
  const [translateY, setTranslateY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [exporting, setExporting] = useState(false);
  
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const [language] = useState('es');

  // Pan handlers
  const handleMouseDown = (e) => {
    if (e.button !== 0) return; // left click only
    setIsDragging(true);
    setDragStart({ x: e.clientX - translateX, y: e.clientY - translateY });
  };
  const handleMouseMove = (e) => {
    if (!isDragging) return;
    setTranslateX(e.clientX - dragStart.x);
    setTranslateY(e.clientY - dragStart.y);
  };
  const handleMouseUp = () => setIsDragging(false);

  // Zoom with mouse wheel
  const handleWheel = (e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    setZoom(prev => Math.min(Math.max(prev * delta, 0.3), 2.5));
  };

  // Reset view
  const resetView = () => {
    setZoom(1);
    setTranslateX(0);
    setTranslateY(0);
  };
  
  const labels = {
    es: {
      title: 'Organigrama',
      subtitle: 'Estructura organizacional interactiva',
      search: 'Buscar empleado...',
      totalEmployees: 'Total Empleados',
      managers: 'Gerentes',
      matrixRelations: 'Relaciones Matriciales',
      noData: 'No hay datos disponibles',
      loading: 'Cargando organigrama...',
      addMatrix: 'Agregar Reporte Matricial',
      selectEmployee: 'Seleccionar empleado',
      selectManager: 'Seleccionar jefe funcional',
      save: 'Guardar',
      cancel: 'Cancelar',
      employeeDetails: 'Detalles del Empleado',
      position: 'Puesto',
      department: 'Departamento',
      level: 'Nivel',
      directManager: 'Jefe Directo',
      functionalManagers: 'Jefes Funcionales',
      directReports: 'Reportes Directos',
      ipsScore: 'SCORE MEVIA',
      viewProfile: 'Ver Perfil',
      removeMatrix: 'Remover'
    },
    en: {
      title: 'Org Chart',
      subtitle: 'Interactive organizational structure',
      search: 'Search employee...',
      totalEmployees: 'Total Employees',
      managers: 'Managers',
      matrixRelations: 'Matrix Relations',
      noData: 'No data available',
      loading: 'Loading org chart...',
      addMatrix: 'Add Matrix Report',
      selectEmployee: 'Select employee',
      selectManager: 'Select functional manager',
      save: 'Save',
      cancel: 'Cancel',
      employeeDetails: 'Employee Details',
      position: 'Position',
      department: 'Department',
      level: 'Level',
      directManager: 'Direct Manager',
      functionalManagers: 'Functional Managers',
      directReports: 'Direct Reports',
      ipsScore: 'MEVIA SCORE',
      viewProfile: 'View Profile',
      removeMatrix: 'Remove'
    }
  };
  const t = labels[language] || labels.es;

  // Fetch org chart data
  useEffect(() => {
    let isMounted = true;
    
    const loadData = async () => {
      try {
        const treeData = await apiCall('/orgchart/tree');
        
        if (isMounted && treeData?.success) {
          setTreeData(treeData.data.roots || []);
          setMatrixRelations(treeData.data.matrix_relations || []);
        }
        
        // Fetch statistics
        try {
          const statsData = await apiCall('/orgchart/statistics');
          if (isMounted && statsData?.success) {
            setStatistics(statsData.data);
          }
        } catch (e) {
          // Stats are optional
        }
      } catch (err) {
        if (isMounted) {
          setError(err.message || 'Error desconocido');
          toast.error('Error al cargar el organigrama');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };
    
    loadData();
    
    return () => {
      isMounted = false;
    };
  }, []);

  // Function to reload org chart data
  const fetchOrgChart = async () => {
    try {
      const treeData = await apiCall('/orgchart/tree');
      if (treeData?.success) {
        setTreeData(treeData.data.roots || []);
        setMatrixRelations(treeData.data.matrix_relations || []);
      }
      const statsData = await apiCall('/orgchart/statistics');
      if (statsData?.success) {
        setStatistics(statsData.data);
      }
    } catch (error) {
      logger.error('[OrgChart] Reload error:', error.message);
    }
  };
  
  const fetchEmployees = async () => {
    try {
      const data = await apiCall('/admin/employees');
      if (data.employees) {
        setEmployees(data.employees);
      }
    } catch (error) {
      logger.error('Error fetching employees:', error);
    }
  };
  
  // Search employees
  useEffect(() => {
    const search = async () => {
      if (searchQuery.length < 2) {
        setSearchResults([]);
        return;
      }
      
      try {
        const data = await apiCall(`/orgchart/search?q=${encodeURIComponent(searchQuery)}`);
        if (data.success) {
          setSearchResults(data.data);
        }
      } catch (error) {
        logger.error('Error searching:', error);
      }
    };
    
    const debounce = setTimeout(search, 300);
    return () => clearTimeout(debounce);
  }, [searchQuery]);
  
  // Handle node click
  const handleNodeClick = async (node) => {
    if (!node.id) return;
    
    setSelectedNode(node);
    setShowDetailPanel(true);
    
    try {
      const data = await apiCall(`/orgchart/employee/${node.id}`);
      if (data.success) {
        setEmployeeDetail(data.data);
      }
    } catch (error) {
      logger.error('Error fetching employee details:', error);
    }
  };
  
  // Add matrix relation
  const handleAddMatrixRelation = async () => {
    if (!selectedEmployee || !selectedManager) return;
    
    try {
      await apiCall('/orgchart/matrix-relation', {
        method: 'POST',
        body: JSON.stringify({
          employee_id: selectedEmployee,
          functional_manager_id: selectedManager
        })
      });
      
      toast.success('Relación matricial agregada');
      setShowMatrixModal(false);
      setSelectedEmployee('');
      setSelectedManager('');
      fetchOrgChart();
    } catch (error) {
      toast.error(error.message || 'Error');
    }
  };
  
  // Remove matrix relation
  const handleRemoveMatrixRelation = async (employeeId, managerId) => {
    try {
      await apiCall(`/orgchart/matrix-relation/${employeeId}/${managerId}`, {
        method: 'DELETE'
      });
      toast.success('Relación matricial eliminada');
      fetchOrgChart();
      if (employeeDetail && employeeDetail.id === employeeId) {
        handleNodeClick({ id: employeeId });
      }
    } catch (error) {
      toast.error(error.message || 'Error');
    }
  };

  // Export org chart to PDF
  const handleExportPDF = async () => {
    if (!chartRef.current) return;
    
    setExporting(true);
    toast.info('Generando PDF...');
    
    try {
      // Temporarily reset zoom for better export quality
      const originalZoom = zoom;
      setZoom(1);
      await new Promise(resolve => setTimeout(resolve, 100));
      
      const element = chartRef.current;
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#f3f4f6'
      });
      
      // Restore original zoom
      setZoom(originalZoom);
      
      const imgData = canvas.toDataURL('image/png');
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      
      // Calculate PDF dimensions (landscape for better fit)
      const pdfWidth = 297; // A4 landscape width in mm
      const pdfHeight = 210; // A4 landscape height in mm
      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
      const scaledWidth = imgWidth * ratio;
      const scaledHeight = imgHeight * ratio;
      
      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4'
      });
      
      // Add header
      pdf.setFontSize(18);
      pdf.setTextColor(20, 184, 166); // teal-500
      pdf.text('Organigrama - ' + (statistics?.total_employees || '') + ' Empleados', 14, 15);
      
      pdf.setFontSize(10);
      pdf.setTextColor(107, 114, 128); // gray-500
      pdf.text('Generado: ' + new Date().toLocaleDateString('es-MX', { 
        year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit'
      }), 14, 22);
      
      // Add the org chart image
      const xOffset = (pdfWidth - scaledWidth) / 2;
      pdf.addImage(imgData, 'PNG', xOffset, 28, scaledWidth * 0.9, scaledHeight * 0.9);
      
      // Save PDF
      pdf.save(`organigrama_${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success('PDF generado exitosamente');
    } catch (error) {
      logger.error('Error generating PDF:', error);
      toast.error('Error al generar PDF');
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="h-12 w-12 animate-spin mx-auto text-teal-600 mb-4" />
          <p className="text-gray-600">{t.loading}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="h-12 w-12 mx-auto text-red-500 mb-4" />
          <p className="text-gray-900 font-semibold">Error al cargar</p>
          <p className="text-gray-600 mt-2">{error}</p>
          <Button 
            onClick={() => window.location.reload()} 
            className="mt-4"
          >
            Reintentar
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-white shadow-sm border-b px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Users className="h-6 w-6 text-teal-600" />
              {t.title}
            </h1>
            <p className="text-sm text-gray-500">{t.subtitle}</p>
          </div>
          
          <div className="flex items-center gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder={t.search}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 w-64"
              />
              
              {/* Search Results Dropdown */}
              {searchResults.length > 0 && (
                <div className="absolute top-full mt-1 w-full bg-white rounded-lg shadow-lg border z-50 max-h-64 overflow-y-auto">
                  {searchResults.map((result) => (
                    <div
                      key={result.id}
                      onClick={() => {
                        handleNodeClick(result);
                        setSearchQuery('');
                        setSearchResults([]);
                      }}
                      className="p-3 hover:bg-gray-50 cursor-pointer border-b last:border-b-0"
                    >
                      <p className="font-medium">{result.name}</p>
                      <p className="text-sm text-gray-500">{result.position} • {result.department}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Add Matrix Button */}
            <Button 
              variant="outline"
              onClick={() => {
                fetchEmployees();
                setShowMatrixModal(true);
              }}
            >
              <Link2 className="h-4 w-4 mr-2" />
              {t.addMatrix}
            </Button>
            
            {/* Zoom Controls */}
            <div className="flex items-center gap-1 border rounded-lg p-1">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setZoom(z => Math.max(0.3, z - 0.1))}
                data-testid="zoom-out-btn"
              >
                <ZoomOut className="h-4 w-4" />
              </Button>
              <span className="text-sm px-2 min-w-[50px] text-center">{Math.round(zoom * 100)}%</span>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setZoom(z => Math.min(2.5, z + 0.1))}
                data-testid="zoom-in-btn"
              >
                <ZoomIn className="h-4 w-4" />
              </Button>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={resetView}
                title="Centrar vista"
                data-testid="reset-view-btn"
              >
                <Maximize2 className="h-4 w-4" />
              </Button>
            </div>
            
            {/* Export PDF Button */}
            <Button 
              onClick={handleExportPDF}
              disabled={exporting}
              className="bg-teal-600 hover:bg-teal-700 text-white"
              data-testid="export-pdf-btn"
            >
              {exporting ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              Exportar PDF
            </Button>
          </div>
        </div>
        
        {/* Statistics */}
        {statistics && (
          <div className="flex gap-6 mt-4 flex-wrap">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Users className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">{t.totalEmployees}</p>
                <p className="text-xl font-bold">{statistics.total_employees}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <Briefcase className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">{t.managers}</p>
                <p className="text-xl font-bold">{statistics.total_managers}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                <Link2 className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">{t.matrixRelations}</p>
                <p className="text-xl font-bold">{statistics.matrix_relations}</p>
              </div>
            </div>
            
            {/* Level Legend */}
            <div className="ml-auto flex items-center gap-3 flex-wrap">
              <span className="text-sm text-gray-500">Niveles:</span>
              {Object.entries(LEVEL_COLORS).filter(([k]) => k !== 'default').map(([level, config]) => (
                <div key={level} className="flex items-center gap-1">
                  <div 
                    className="w-4 h-4 rounded"
                    style={{ backgroundColor: config.bg }}
                  />
                  <span className="text-xs text-gray-600">{config.name}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      
      {/* Viewport (fixed size, overflow hidden) */}
      <div 
        ref={containerRef}
        className="bg-gray-50"
        style={{ 
          width: '100%',
          height: 'calc(100vh - 200px)',
          overflow: 'hidden',
          position: 'relative',
          cursor: isDragging ? 'grabbing' : 'grab',
          userSelect: 'none'
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onWheel={handleWheel}
        data-testid="orgchart-viewport"
      >
        {/* Canvas (receives transform) */}
        <div 
          ref={chartRef}
          className="p-8"
          style={{ 
            transform: `scale(${zoom}) translate(${translateX / zoom}px, ${translateY / zoom}px)`,
            transformOrigin: '0 0',
            position: 'absolute',
            minWidth: '100%'
          }}
          data-testid="orgchart-canvas"
        >
          {treeData.length > 0 ? (
            <div className="flex gap-8 justify-center">
              {treeData.map((root) => (
                <OrgNode 
                  key={root.id} 
                  node={root} 
                  onSelect={handleNodeClick}
                  isSelected={selectedNode?.id === root.id}
                />
              ))}
            </div>
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="text-center text-gray-500">
                <Users className="h-16 w-16 mx-auto mb-4 opacity-30" />
                <p>{t.noData}</p>
                <p className="text-sm mt-2">Configure la estructura organizacional en Configuraciones</p>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Employee Detail Panel */}
      <Dialog open={showDetailPanel} onOpenChange={setShowDetailPanel}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              {t.employeeDetails}
            </DialogTitle>
          </DialogHeader>
          
          {employeeDetail ? (
            <div className="space-y-4">
              {/* Header */}
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-teal-100 flex items-center justify-center text-2xl font-bold text-teal-600">
                  {employeeDetail.name?.charAt(0) || '?'}
                </div>
                <div>
                  <h3 className="text-lg font-semibold">{employeeDetail.name}</h3>
                  <p className="text-sm text-gray-500">{employeeDetail.email}</p>
                </div>
              </div>
              
              {/* IPS Score */}
              {employeeDetail.ips_score !== null && employeeDetail.ips_status && (
                <div className={`p-3 rounded-lg flex items-center justify-between ${
                  employeeDetail.ips_status === 'healthy' ? 'bg-green-50' :
                  employeeDetail.ips_status === 'at_risk' ? 'bg-yellow-50' : 'bg-red-50'
                }`}>
                  <div className="flex items-center gap-2">
                    {employeeDetail.ips_status === 'healthy' ? (
                      <CheckCircle className="h-5 w-5 text-green-600" />
                    ) : employeeDetail.ips_status === 'at_risk' ? (
                      <AlertCircle className="h-5 w-5 text-yellow-600" />
                    ) : (
                      <AlertTriangle className="h-5 w-5 text-red-600" />
                    )}
                    <span className="font-medium">{t.ipsScore}</span>
                  </div>
                  <span className="text-2xl font-bold">
                    {Math.round(employeeDetail.ips_score)}
                  </span>
                </div>
              )}
              
              {/* Info Grid */}
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500">{t.position}</p>
                  <p className="font-medium">{employeeDetail.position || '-'}</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500">{t.department}</p>
                  <p className="font-medium">{employeeDetail.department || '-'}</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500">{t.level}</p>
                  <p className="font-medium">{employeeDetail.level_name || `Nivel ${employeeDetail.level || '-'}`}</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500">{t.directReports}</p>
                  <p className="font-medium">{employeeDetail.direct_reports_count}</p>
                </div>
              </div>
              
              {/* Direct Manager */}
              {employeeDetail.direct_manager && (
                <div>
                  <p className="text-sm font-medium text-gray-500 mb-2">{t.directManager}</p>
                  <div className="flex items-center gap-2 p-2 bg-blue-50 rounded-lg">
                    <div className="w-8 h-8 rounded-full bg-blue-200 flex items-center justify-center text-sm font-bold text-blue-700">
                      {employeeDetail.direct_manager.name?.charAt(0)}
                    </div>
                    <span className="font-medium">{employeeDetail.direct_manager.name}</span>
                  </div>
                </div>
              )}
              
              {/* Functional Managers */}
              {employeeDetail.functional_managers?.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-gray-500 mb-2">{t.functionalManagers}</p>
                  <div className="space-y-2">
                    {employeeDetail.functional_managers.map((mgr) => (
                      <div 
                        key={mgr.id}
                        className="flex items-center justify-between p-2 bg-purple-50 rounded-lg"
                      >
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-purple-200 flex items-center justify-center text-sm font-bold text-purple-700">
                            {mgr.name?.charAt(0)}
                          </div>
                          <span className="font-medium">{mgr.name}</span>
                          <Badge variant="outline" className="text-xs">{mgr.type}</Badge>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveMatrixRelation(employeeDetail.id, mgr.id)}
                        >
                          <Unlink className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Direct Reports List */}
              {employeeDetail.direct_reports?.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-gray-500 mb-2">
                    {t.directReports} ({employeeDetail.direct_reports.length})
                  </p>
                  <div className="max-h-32 overflow-y-auto space-y-1">
                    {employeeDetail.direct_reports.map((report) => (
                      <div 
                        key={report.id}
                        className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100"
                        onClick={() => handleNodeClick({ id: report.id })}
                      >
                        <div className="w-6 h-6 rounded-full bg-gray-300 flex items-center justify-center text-xs font-bold">
                          {report.name?.charAt(0)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{report.name}</p>
                          <p className="text-xs text-gray-500 truncate">{report.position}</p>
                        </div>
                        <ChevronRight className="h-4 w-4 text-gray-400" />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="py-8 text-center text-gray-500">
              <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2" />
              Cargando...
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Add Matrix Relation Modal */}
      <Dialog open={showMatrixModal} onOpenChange={setShowMatrixModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t.addMatrix}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium">{t.selectEmployee}</label>
              <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
                <SelectTrigger>
                  <SelectValue placeholder={t.selectEmployee} />
                </SelectTrigger>
                <SelectContent>
                  {employees.map((emp) => (
                    <SelectItem key={emp.id} value={emp.id}>
                      {emp.nombre} ({emp.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">{t.selectManager}</label>
              <Select value={selectedManager} onValueChange={setSelectedManager}>
                <SelectTrigger>
                  <SelectValue placeholder={t.selectManager} />
                </SelectTrigger>
                <SelectContent>
                  {employees
                    .filter(e => e.id !== selectedEmployee)
                    .map((emp) => (
                      <SelectItem key={emp.id} value={emp.id}>
                        {emp.nombre} ({emp.email})
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMatrixModal(false)}>
              {t.cancel}
            </Button>
            <Button 
              onClick={handleAddMatrixRelation}
              disabled={!selectedEmployee || !selectedManager}
            >
              {t.save}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrganizationalChart;
